import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';

import { useAppDispatch, useAppSelector } from '@store/store.tsx';
import { setTheme, selectTheme, type ThemeMode } from '@store/slices/uiSlice.ts';

export interface ThemeModeContextValue {
  themeMode: ThemeMode;
  resolvedTheme: 'light' | 'dark';
  isDark: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  themeConfig: ThemeConfig;
}

const DARK_META_COLOR = '#101316';
const LIGHT_META_COLOR = '#edf2f7';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

export const THEME_TOKENS = {
  light: {
    colorBgBase: '#edf2f7',
    colorBgLayout: '#edf2f7',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBorder: '#bdc9d6',
    colorBorderSecondary: '#d7e0ea',
    colorTextBase: '#172033',
    colorText: '#172033',
    colorTextSecondary: '#4c5c6d',
    colorTextTertiary: '#667789',
    colorFill: 'rgba(23, 32, 51, 0.14)',
    colorFillSecondary: 'rgba(23, 32, 51, 0.1)',
    colorFillTertiary: 'rgba(23, 32, 51, 0.06)',
    colorFillQuaternary: 'rgba(23, 32, 51, 0.04)',
    colorPrimary: '#1677ff',
    colorPrimaryBg: '#e7f1ff',
    colorPrimaryBgHover: '#d7e9ff',
    colorSuccessBg: '#e9f8ec',
    colorSuccessBorder: '#8fcf9a',
    colorErrorBg: '#fff0f0',
    colorErrorBorder: '#ff9c9c',
    colorWarningBg: '#fff6df',
    colorWarningBorder: '#e6b84d',
    boxShadow: '0 2px 8px rgba(23, 32, 51, 0.1)',
    boxShadowSecondary: '0 6px 18px rgba(23, 32, 51, 0.12)',
    boxShadowTertiary: '0 1px 4px rgba(23, 32, 51, 0.12)',
  },
  dark: {
    colorBgBase: '#101316',
    colorBgLayout: '#101316',
    colorBgContainer: '#1b222a',
    colorBgElevated: '#202832',
    colorBorder: 'rgba(213, 222, 230, 0.24)',
    colorBorderSecondary: 'rgba(255, 255, 255, 0.12)',
    colorTextBase: '#f3f6f8',
    colorText: '#f3f6f8',
    colorTextSecondary: '#aab4be',
    colorTextTertiary: '#8793a0',
    colorFill: 'rgba(255, 255, 255, 0.18)',
    colorFillSecondary: 'rgba(255, 255, 255, 0.12)',
    colorFillTertiary: 'rgba(255, 255, 255, 0.08)',
    colorFillQuaternary: 'rgba(255, 255, 255, 0.05)',
    colorPrimary: '#00c2b2',
    colorPrimaryBg: '#123332',
    colorPrimaryBgHover: '#17433f',
    colorSuccessBg: '#16351f',
    colorSuccessBorder: '#4d8c5d',
    colorErrorBg: '#3b1d1d',
    colorErrorBorder: '#a15a5a',
    colorWarningBg: '#3a2d14',
    colorWarningBorder: '#aa8545',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.24)',
    boxShadowSecondary: '0 8px 24px rgba(0, 0, 0, 0.28)',
    boxShadowTertiary: '0 1px 4px rgba(0, 0, 0, 0.32)',
  },
} as const;

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

const FALLBACK_THEME_CONFIG: ThemeConfig = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {},
};

const FALLBACK_CONTEXT: ThemeModeContextValue = {
  themeMode: 'system',
  resolvedTheme: 'light',
  isDark: false,
  setThemeMode: () => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('useThemeMode fallback: no ThemeModeProvider found.');
    }
  },
  themeConfig: FALLBACK_THEME_CONFIG,
};

const getSystemPreference = (): 'light' | 'dark' => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
};

const ensureThemeColorMeta = (): HTMLMetaElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const head = document.head || document.getElementsByTagName('head')[0];
  if (!head) {
    return null;
  }

  let meta = head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    head.appendChild(meta);
  }

  return meta;
};

export const ThemeModeProvider = ({ children }: PropsWithChildren) => {
  const themeMode = useAppSelector(selectTheme);
  const dispatch = useAppDispatch();
  const [systemPreference, setSystemPreference] = useState<'light' | 'dark'>(() => getSystemPreference());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const media = window.matchMedia(MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPreference(event.matches ? 'dark' : 'light');
    };

    const addListener = media.addEventListener?.bind(media) ?? media.addListener?.bind(media) ?? null;
    const removeListener = media.removeEventListener?.bind(media) ?? media.removeListener?.bind(media) ?? null;

    setSystemPreference(media.matches ? 'dark' : 'light');
    addListener?.('change', handleChange);

    return () => {
      removeListener?.('change', handleChange);
    };
  }, []);

  const resolvedTheme = themeMode === 'system' ? systemPreference : themeMode;
  const isDark = resolvedTheme === 'dark';

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const root = document.documentElement;
    root.setAttribute('data-theme', resolvedTheme);
    root.style.colorScheme = resolvedTheme;

    const meta = ensureThemeColorMeta();
    if (meta) {
      meta.setAttribute('content', isDark ? DARK_META_COLOR : LIGHT_META_COLOR);
    }
  }, [resolvedTheme, isDark]);

  const setThemeMode = useCallback(
    (mode: ThemeMode) => {
      dispatch(setTheme(mode));
    },
    [dispatch],
  );

  const themeConfig = useMemo<ThemeConfig>(() => {
    const palette = isDark ? THEME_TOKENS.dark : THEME_TOKENS.light;

    return {
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        ...palette,
      },
    } satisfies ThemeConfig;
  }, [isDark]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({ themeMode, resolvedTheme, isDark, setThemeMode, themeConfig }),
    [themeMode, resolvedTheme, isDark, setThemeMode, themeConfig],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = (): ThemeModeContextValue => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    return FALLBACK_CONTEXT;
  }
  return ctx;
};
